import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import Sublayer from '@arcgis/core/layers/support/Sublayer';
import Symbol from '@arcgis/core/symbols/Symbol';
import {
  combineLatest,
  defaultIfEmpty,
  finalize,
  from,
  map,
  mergeMap,
  Observable,
  of,
  Subscription,
  switchMap,
  merge,
  takeUntil,
  Subject,
  defer,
  startWith,
} from 'rxjs';
import { LoadingController } from 'app/shared/helpers/loading.controller';
import { EsriUtils, SublayerTypes } from 'app/shared/utils/esri.utils';
import { RadioItemLike } from '../../../radio-group/radio-group.component';
import { MainMapService } from 'app/shared/components/main-map/main-map.service';
import { AppConfigService } from 'app/shared/services/app-config.service';
import { TocLayerItem, TocService } from '../../toc.service';
import { ExtractExpr } from 'app/shared/utils/item-expr.utils';
import { ScreenService } from 'app/shared/services/screen.service';

interface LayerItemLike extends RadioItemLike {
  data?: any;
}

const defaultDistance = 10;

@Component({
  selector: 'app-layer-list-effect',
  templateUrl: 'layer-list-effect.component.html',
  styleUrls: ['layer-list-effect.component.scss'],
})
export class LayerListEffectComponent implements OnInit, OnChanges, OnDestroy {
  private _layerSelected: __esri.Layer | SublayerTypes;
  private _layerItemSelected: LayerItemLike;

  get layerSelected() {
    return this._layerSelected;
  }

  set layerSelected(value) {
    if (this._layerSelected !== value) {
      this._layerSelected = value;

      this._layerItemSelected = this.layerItemList?.find(
        (x) => x.data?.layer === value
      );
    }
  }

  get layerItemSelected() {
    return this._layerItemSelected;
  }

  set layerItemSelected(value) {
    this.layerSelected = value?.data?.layer;
  }

  distance = defaultDistance;

  layerItemList: LayerItemLike[];

  @Output()
  onLayerEnabledChanged = new EventEmitter<any>(true);

  @Input()
  distanceLabel: string;

  @Input()
  loading = false;

  @Input()
  allowLayerFn: (
    layer: __esri.FeatureLayer | __esri.Sublayer
  ) => Observable<boolean> | boolean;

  isLayerEnabled = false;

  get siiaMode() {
    return this.appConfig.siiaMode;
  }

  valueExpr: ExtractExpr<LayerItemLike> = (item) => {
    return item?.data?.layer;
  };

  loadingController = new LoadingController();

  private layerListSubscription: Subscription;

  isMobileModeObservable = this.screenService.observeIsMobile();

  private lifecycle = new Subject<void>();

  constructor(
    private appConfig: AppConfigService,
    private mainMapService: MainMapService,
    private tocService: TocService,
    private screenService: ScreenService
  ) {}

  ngOnInit(): void {
    this.mainMapService.onRequestClear
      .pipe(takeUntil(this.lifecycle))
      .subscribe(() => {
        this.layerItemSelected = null;
        this.distance = defaultDistance;

        if (this.isLayerEnabled) {
          this.isLayerEnabled = false;
          this.onLayerEnabledChanged.emit(null);
        }
      });

    this.handleLayerList();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.loading) {
      if (this.loading) {
        this.loadingController.show();
      } else {
        this.loadingController.hide();
      }
    }

    if (changes.showIcon && !changes.showIcon.firstChange) {
      this.handleLayerList();
    }
  }

  ngOnDestroy(): void {
    this.lifecycle.next();
    this.lifecycle.complete();

    this.layerListSubscription?.unsubscribe();
  }

  onItemSelected() {
    if (this.layerItemSelected) {
      this.isLayerEnabled = true;

      this.onLayerEnabledChanged.emit({
        layerItem: this.layerItemSelected,
        distance: this.distance,
      });
    } else {
      this.isLayerEnabled = false;
      this.onLayerEnabledChanged.emit(null);
    }
  }

  onDistanceChanged(distance: number) {
    if (this.isLayerEnabled) {
      this.onLayerEnabledChanged.emit({
        layerItem: this.layerItemSelected,
        distance,
      });
    }
  }

  private handleLayerList() {
    this.layerListSubscription?.unsubscribe();
    this.layerListSubscription = this.getLayerListObservable().subscribe(
      (list) => {
        this.layerItemList = list;

        if (
          this.isLayerEnabled &&
          !this.layerItemList?.some(
            (x) => x?.data?.layer === this.layerSelected
          )
        ) {
          this.isLayerEnabled = false;
          this.layerItemSelected = null;
          this.onLayerEnabledChanged.emit(null);
        }
      }
    );
  }

  private getLayerListObservable() {
    const observeVisibleLayersRecursive = (
      layerOrLayers:
        | __esri.Layer
        | SublayerTypes
        | Observable<__esri.Layer | SublayerTypes>
        | Array<
            | __esri.Layer
            | SublayerTypes
            | Observable<__esri.Layer | SublayerTypes>
          >
    ) => {
      if (!Array.isArray(layerOrLayers)) {
        layerOrLayers = [layerOrLayers];
      }

      const observableList: Observable<Array<LayerItemLike>>[] =
        layerOrLayers.map((layerObservable) => {
          if (!(layerObservable instanceof Observable)) {
            layerObservable = of(layerObservable);
          }

          return layerObservable.pipe(
            switchMap((layer) => {
              if (!layer) {
                return of(null);
              }

              let observable: Observable<Array<LayerItemLike>>;

              let layerVisibleObservable =
                EsriUtils.isLayerVisibleObservable(layer);

              const collection = EsriUtils.getSublayersCollection(layer as any);

              if (collection) {
                const getArray = () => collection.toArray().reverse();
                const observableSublayers = merge(
                  defer(() => of(getArray())),
                  EsriUtils.on(collection, 'change').pipe(map(getArray))
                ).pipe(
                  switchMap((sublayers) => {
                    if (!sublayers.length) {
                      return of([layer]);
                    }

                    return observeVisibleLayersRecursive(sublayers);
                  }),
                  map((res) => {
                    return res.flat();
                  })
                );

                observable = layerVisibleObservable.pipe(
                  switchMap((visible) => {
                    if (!visible) {
                      return of([]);
                    }

                    return observableSublayers;
                  })
                );
              } else {
                observable = layerVisibleObservable.pipe(
                  switchMap((visible) => {
                    if (!visible) {
                      return of([]);
                    }

                    this.loadingController.show();
                    return this.parseLayerWithIconAsSelectableItem(layer).pipe(
                      map((x) => [x]),
                      finalize(() => {
                        this.loadingController.hide();
                      })
                    );
                  })
                );
              }

              return observable;
            })
          );
        });

      return combineLatest(observableList);
    };

    const layerItemObservableList = this.tocService
      .getLayerMenuItems()
      .flatMap((menuItem) => {
        return this.tocService
          .getLayerItemsByMenu(menuItem)
          .map((x) => {
            if (x instanceof TocLayerItem) {
              if (x.layer) {
                return of(x.layer);
              }

              return x.whenLayerLoaded.pipe(startWith(null));
            }

            return of(x);
          })
          .reverse();
      });

    return observeVisibleLayersRecursive(layerItemObservableList).pipe(
      map((res) => {
        return res.flat().filter((x) => !!x);
      }),
      defaultIfEmpty([])
    );
  }

  private parseLayerWithIconAsSelectableItem(
    layer: __esri.Layer | SublayerTypes
  ): Observable<LayerItemLike> {
    if (!(layer instanceof Sublayer || layer instanceof FeatureLayer)) {
      return of(null);
    }

    let allowLayerObservable: Observable<boolean>;
    if (this.allowLayerFn) {
      let res = this.allowLayerFn(layer);
      if (typeof res === 'boolean') {
        allowLayerObservable = of(res);
      }

      if (res instanceof Observable) {
        allowLayerObservable = res;
      }
    }

    if (!allowLayerObservable) {
      allowLayerObservable = of(true);
    }

    return allowLayerObservable.pipe(
      mergeMap((aBool) => {
        if (!aBool) {
          return of(null);
        }

        return new Observable<LayerItemLike>((subscriber) => {
          const item: LayerItemLike = {
            label: layer.title,
            iconAs: 'svg',
            data: { layer },
          };

          let observableIcon: Observable<string>;
          if (this.siiaMode) {
            const symbol = ((layer as any)?.renderer as __esri.SimpleRenderer)
              ?.symbol;

            if (symbol instanceof Symbol) {
              observableIcon = from(
                EsriUtils.renderSymbolPreviewImageUrl(symbol)
              );
            }
          }

          if (!observableIcon) {
            observableIcon = of(null);
          }

          subscriber.add(
            observableIcon.subscribe((icon) => {
              item.icon = icon;

              subscriber.next(item);
              subscriber.complete();
            })
          );
        });
      })
    );
  }
}
